// mindmetal-app/web/src/hooks/useMindMetaLTestUsers.ts
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@personic/shared'
import type {
  TestUser,
  TestUserListResponse,
  CreateTestUserRequest,
  DistillationRequest,
  DistillationResult,
  MindMetaLProfile,
} from '@personic/shared'

const QUERY_KEYS = {
  testUsers: (page: number, limit: number) => ['mindmetal-test-users', { page, limit }],
  testUser: (id: string) => ['mindmetal-test-user', id],
  profile: (id: string) => ['mindmetal-profile', id],
}

export function useTestUsers(page = 1, limit = 20) {
  return useQuery({
    queryKey: QUERY_KEYS.testUsers(page, limit),
    queryFn: async () => {
      const { data } = await api.get<TestUserListResponse>(
        `/api/mindmetal/v3/admin/test-users?page=${page}&limit=${limit}`
      )
      return data
    },
  })
}

export function useTestUser(canonicalId: string) {
  return useQuery({
    queryKey: QUERY_KEYS.testUser(canonicalId),
    queryFn: async () => {
      const { data } = await api.get<TestUser>(
        `/api/mindmetal/v3/admin/test-users/${canonicalId}`
      )
      return data
    },
    enabled: !!canonicalId,
  })
}

export function useTestUserProfile(canonicalId: string) {
  return useQuery({
    queryKey: QUERY_KEYS.profile(canonicalId),
    queryFn: async () => {
      const { data } = await api.get<MindMetaLProfile>(
        `/api/mindmetal/v3/admin/test-users/${canonicalId}/profile`
      )
      return data
    },
    enabled: !!canonicalId,
  })
}

export function useCreateTestUser() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (input: CreateTestUserRequest) => {
      const { data } = await api.post<TestUser>('/api/mindmetal/v3/admin/test-users', input)
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['mindmetal-test-users'] })
    },
  })
}

export function useDeleteTestUser() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (canonicalId: string) => {
      await api.delete(`/api/mindmetal/v3/admin/test-users/${canonicalId}`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['mindmetal-test-users'] })
    },
  })
}

export function useRunDistillation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ canonicalId, input }: { canonicalId: string; input: DistillationRequest }) => {
      const { data } = await api.post<DistillationResult>(
        `/api/mindmetal/v3/admin/test-users/${canonicalId}/distill`,
        input
      )
      return data
    },
    onSuccess: (_, { canonicalId }) => {
      queryClient.invalidateQueries({ queryKey: ['mindmetal-test-users'] })
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.testUser(canonicalId) })
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.profile(canonicalId) })
    },
  })
}

export async function exportProfile(canonicalId: string, format: 'yaml' | 'json' | 'csv') {
  const response = await api.get(
    `/api/mindmetal/v3/admin/test-users/${canonicalId}/export?format=${format}`,
    { responseType: 'blob' }
  )
  return response.data
}
